import { z } from 'zod'
import { requiredString, timestampedDocSchema } from '../utils/basicSchemas/document.z.schema'
import { mongoId } from '../utils/basicSchemas/_id.z.schema'
import { ZSchema } from '../utils/zodSchema'

/**
 * Zod schema representing additional data structures associated with the {@link detailsSchema}.
 * 
 * Fields:
 * - `other`: An array of objects each containing:
 *   - `type`: An object with:
 *     - `label`: A required string to describe the type.
 *     - `value`: A placeholder for mixed/any type data. Requires further clarification.
 * 
 * @example
 * otherSchema.parse({
 *     other: [{ type: { label: "Example", value: "Sample Value" }}]
 * });
 */
const otherSchema = z.object({
    other: z.array(
        z.object({
            type: z.object({
                label: requiredString("Other type label"),
                value: z.any(), // TODO: Better type
            }),
        })
    ),
})

/**
 * Zod schema representing detailed information.
 * 
 * Fields:
 * - `creator`: A Mongo ObjectId referencing the entity creating the details.
 * - `post`: A Mongo ObjectId referencing the associated post.
 * - `category`: A nullable string representing the category of details. Defaults to null.
 * - `performanceTime`: An object detailing various dates/times. Includes:
 *   - `start`, `end`, `completion`: Optional dates.
 *   - `time`: An optional string.
 *   - Also merges fields from {@link otherSchema}.
 * - `details`: A placeholder for mixed/any type data. Requires further clarification.
 * 
 * @example
 * detailsSchema.parse({
 *     creator: "603f650a0953de235c89eadc",
 *     post: "603f650a0953de235c89eadb",
 *     category: "CategoryName",
 *     performanceTime: { start: new Date(), time: "10:00 AM", other: [{ type: { label: "Example", value: "Sample Value" }}] },
 * });
 */
const detailsSchemaFull = z.object({
    creator: mongoId("Creator"),
    post: mongoId("Post"),
    category: z.string({ required_error: "Details category is required" }).nullable().default(null),
    performanceTime: z.object({
        start: z.date().optional(),
        end: z.date().optional(),
        completion: z.date().optional(),
        time: z.string().optional(),
    }).merge(otherSchema),
    details: z.any().nullable().default(null), // TODO: Better type
})

/**
 * Zod schema representing a Details document.
 * 
 * This schema extends {@link detailsSchema} by merging with {@link timestampedDocSchema} 
 * to include fields inherent to Mongoose documents like timestamps, `_id`, and `__v`.
 * 
 * @example
 * detailsSchemaDoc.parse({
 *     _id: "603f650a0953de235c89eadc",
 *     __v: "1",
 *     createdAt: new Date(),
 *     updatedAt: new Date(),
 *     creator: "603f650a0953de235c89eadc",
 *     post: "603f650a0953de235c89eadb",
 *     category: "CategoryName",
 *     performanceTime: { start: new Date(), time: "10:00 AM", other: [{ type: { label: "Example", value: "Sample Value" }}] },
 * });
 */
const detailsSchemaDoc = timestampedDocSchema.merge(detailsSchemaFull)

const detailsSchema = z.object({

    categories: z.array(z.any(),
        { required_error: "Post categories is required" }).nonempty().optional(),
    creator: z.array(z.any(),
        { required_error: "Post creator is required" }).nonempty().optional(),
    performanceTime: z.object({},
        { required_error: "No performance time found" }).optional(),
    details: z.object({},
        { required_error: "No details found" }).optional(),
    dates: z.object({},
        { required_error: "No dates supplied " }).optional(),
    text: z.string()
        .min(1, "Post text search must be at least one character")
        .max(30, "Post text searchmust be at most 30 characters")
        .regex(/^(?=.*[a-z])[ 0-9a-zA-Z]{1,30}$/i, "Post text search is invalid"),
})


const detailsValidator = new ZSchema({ bodySchema: detailsSchema })

export { detailsValidator }

// Infer and Export types 
type DetailsSchemaDocType = z.infer<typeof detailsSchemaDoc>
type DetailsSchemaType = z.infer<typeof detailsSchema>

export { detailsSchema, detailsSchemaDoc }
export { DetailsSchemaType, DetailsSchemaDocType }
